import i18next from 'i18next';

import API from '../../../../lib/API';
import { trackOptimizelyCustomEvent, trackRegistrationEvent } from './tracker';
import {
  DepartmentCategory,
  Grade,
  College,
  CollegeDepartment,
} from '../interfaces/school';
import { UseFormSetValue } from 'react-hook-form';

export const getAuthenticityToken = (): string => {
  const metaTag = document.querySelectorAll('meta[name="csrf-token"]')[0];
  if (metaTag) {
    return `${metaTag.getAttribute('content')}`;
  }
  // In test environment, Rails doesn't generate the meta tag
  return '';
};

export const sendVerificationCode = (
  email: string,
  gradeId: string,
): Promise<Response> => {
  return API.post('/api/verifications', {
    verification: {
      email,
      grade_id: gradeId,
    },
  });
};

export const resendVerificationCode = async (
  email: string,
  gradeId: string,
  gradeCode: string,
  setValue: UseFormSetValue<any>,
): Promise<void> => {
  const response = await sendVerificationCode(email, gradeId);
  const json = await response.json();

  if (response.status === 201) {
    trackRegistrationEvent(4, gradeCode, 'retry');
    trackOptimizelyCustomEvent('mail_retry');
    setValue('message', json.message, { shouldTouch: true });
  } else if (response.status === 400) {
    trackOptimizelyCustomEvent('mail_auth_failed');
    setValue('unexpected_error', json.error, { shouldTouch: true });
  } else {
    setValue(
      'unexpected_error',
      i18next.t('registration.sign_up.new.errors.unexpected_error'),
      { shouldTouch: true },
    );
  }
};

interface TextValidationResponse {
  disallowed_char: {
    contains: boolean;
    targets: string[];
  };
}

interface UserDuplicationResponse {
  is_user_exists: boolean;
}

export const validateDisallowedCharsWithApi = async (
  value: string,
  allowAscii: boolean = false,
  allowHankakuKatakana: boolean = false,
) => {
  if (!value) {
    return { disallowed_char: { contains: false, targets: [] } };
  } // To reduce needless API call.

  const queryParams = [
    // 同じ文字を含む異なる文字列で、cache が効くようにユニークな文字だけを送る
    [
      'subject',
      Array.from(
        new Set(
          Array.from(
            new Intl.Segmenter('ja', { granularity: 'grapheme' }).segment(
              value,
            ),
          ).map((v) => v.segment),
        ),
      )
        .sort()
        .join(''),
    ],
    ['allow_ascii', allowAscii.toString()],
    ['allow_hankaku_katakana', allowHankakuKatakana.toString()],
  ]
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

  const response = await API.get(`/api/users/text_validation?${queryParams}`);
  const json: TextValidationResponse = await response.json();
  return json;
};

export const validateDuplicated = async (value?: string) => {
  if (!value) {
    return { is_user_exists: false };
  } // To reduce needless API call.

  const response = await API.get(
    `/api/users/exists?username_or_email=${encodeURIComponent(value)}`,
  );
  const json: UserDuplicationResponse = await response.json();
  return json;
};

export const getDepartmentCategories = async () => {
  const response = await API.get('/api/department_categories_of_interest');
  const json: DepartmentCategory[] = await response.json();
  return json;
};

export const getGrades = async () => {
  const response = await API.get('/api/grades');
  const json: Grade[] = await response.json();
  return json;
};

export const getColleges = async () => {
  const response = await API.get('/api/colleges');
  const json: College[] = await response.json();
  return json;
};

export const getCollegeDepartments = async (collegeId: string) => {
  const response = await API.get(
    `/api/college_departments?college_id=${collegeId}`,
  );
  const json: CollegeDepartment[] = await response.json();
  return json;
};
